import React, { useCallback, useEffect, useRef, useState } from 'react';

enum Direction {
    NEXT = 'next',
    PREV = 'prev',
}

const TAB_LIST_SELECTOR = '[role="tablist"]';
const SCROLL_PERCENTAGE = 0.2;

const getTabList = (parent: HTMLElement | null) => {
    return parent?.querySelector(TAB_LIST_SELECTOR) as HTMLElement | null;
};

const removeListeners =
    (
        listener: () => void,
        tabListElement: HTMLElement,
        resizeObserver: ResizeObserver,
    ) =>
    () => {
        tabListElement.removeEventListener('scroll', listener);
        resizeObserver.unobserve(tabListElement);
    };

const initializeListeners = (
    updateButtonStates: () => void,
    tabListElement: HTMLElement,
) => {
    tabListElement.addEventListener('scroll', updateButtonStates);
    const resizeObserver = new ResizeObserver(updateButtonStates);
    resizeObserver.observe(tabListElement);

    return {
        listener: updateButtonStates,
        cleanup: removeListeners(
            updateButtonStates,
            tabListElement,
            resizeObserver,
        ),
    };
};

const haltListeners = (element: HTMLElement, listener: (() => void) | null) => {
    if (listener) {
        element.removeEventListener('scroll', listener);
    }
};

const scroll = (element: HTMLElement, direction: Direction) => {
    const isNext = direction === Direction.NEXT;
    const directionValue = isNext ? 1 : -1;
    const scrollAmount =
        element.clientWidth * SCROLL_PERCENTAGE * directionValue;

    element.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
    });
};

const resumeListeners = (
    element: HTMLElement,
    listener: (() => void) | null,
    scrollTimer: React.MutableRefObject<NodeJS.Timeout | undefined>,
    onScrollEnd: () => void,
) => {
    clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
        onScrollEnd();
        if (listener) {
            element.addEventListener('scroll', listener);
        }
    }, 500);
};

export const useAgendaScroll = (tabRef: React.RefObject<HTMLDivElement>) => {
    const [canPrev, setCanPrev] = useState<boolean>(false);
    const [canNext, setCanNext] = useState<boolean>(false);
    const tabListRef = useRef<HTMLElement | null>(null);
    const scrollEndTimer = useRef<NodeJS.Timeout>();
    const listenerRef = useRef<(() => void) | null>(null);

    const updateButtonStates = useCallback(() => {
        const el = tabListRef.current;
        if (!el) return;

        const isAtStart = el.scrollLeft <= 1;
        setCanPrev(!isAtStart);

        const maxScrollLeft = el.scrollWidth - el.clientWidth;
        const isAtEnd = el.scrollLeft >= maxScrollLeft - 1;
        setCanNext(!isAtEnd);
    }, []);

    useEffect(() => {
        const tabListElement = getTabList(tabRef.current);
        if (!tabListElement) return;

        tabListRef.current = tabListElement;

        const { listener, cleanup } = initializeListeners(
            updateButtonStates,
            tabListElement,
        );
        listenerRef.current = listener;

        updateButtonStates();

        return cleanup;
    }, [tabRef, updateButtonStates]);

    const scrollContainer = useCallback(
        (direction: Direction) => {
            const el = tabListRef.current;
            if (!el) return;
            const listener = listenerRef.current;

            haltListeners(el, listener);
            scroll(el, direction);
            resumeListeners(el, listener, scrollEndTimer, updateButtonStates);
        },
        [updateButtonStates],
    );

    const onNext = () => canNext && scrollContainer(Direction.NEXT);
    const onPrev = () => canPrev && scrollContainer(Direction.PREV);

    return { canPrev, canNext, onPrev, onNext };
};
